import EmbeddedVideosFilterForm from "components/filterForms/EmbeddedVideosFilterForm/EmbeddedVideosFilterForm";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type ComponentType, useMemo, useState } from "react";
import { batch, connect, type ConnectedProps } from "react-redux";
import { type ContentTab, ContentView } from "../../../../../../components";
import {
  AssessmentFilterForm,
  LinkFilterForm,
  PdfsFilterForm,
  SurveyFilterForm,
  VideosFilterForm,
} from "../../../../../../components/filterForms";
import { EmailFilterForm } from "../../../../../../components/filterForms/EmailFilterForm/EmailFilterForm";
import { EventFilterForm } from "../../../../../../components/filterForms/EventFilterForm/EventFilterForm";
import MessagesFilterForm from "../../../../../../components/filterForms/MessagesFilterForm/MessagesFilterForm";
import DragNDropItemTypes from "../../../../../../enums/dragNDropItemTypes";
import { bindAction } from "../../../../../../interfaces";
import { type AppDispatch, type RootState } from "../../../../../Application/globaltypes/redux";
import { flowDesignerSelector } from "../../../state/selectors";
import {
  activeViewTabSelector,
  flowInspectorTypeSelectSelector,
  isFlowReadOnlySelector,
  itemsToDropFiltersSelector,
  itemsToDropSelector,
} from "../../selectors";
import { setActiveTab, setEntityType } from "../../state/slices/flowDesignerContextSlice";
import {
  resetAppliedFilter,
  resetFilter,
  setSearch as setSearchAction,
} from "../../state/slices/flowDesignerFiltersSlice";
import { reset as resetItemsAction } from "../../state/slices/flowDesignerItemsToDropSlice";
import { fetchItemsToDropV2 } from "../../state/thunks/flowDesignerItemsToDropThunk";
import { type EntityType } from "../../types";
import FlowContentFilterSidebarContainer from "./FlowContentFilterSidebarContainer";
import "./flowContentView.scss";
import { getContentViewTabsOptions } from "./PanelComponent/helpers/contentViewOptions";
import PanelComponent from "./PanelComponent/PanelComponent";

export function FlowContentView(props: PropsFromRedux) {
  const [showFilter, setShowFilter] = useState(false);
  const {
    filters,
    resetAppliedFilters,
    dispose,
    isReadOnly,
    activeViewTab,
    changeViewTab,
    loadItemsV2,
    itemsToDrop,
    search,
    entityTypeSelect,
    setSearch,
    changeEntityType,
    resetItems,
  } = props;

  const onTabChange = (_: React.MouseEvent<HTMLDivElement>, r: { activeIndex: number }) => {
    if (r.activeIndex !== activeViewTab) {
      changeViewTab(r.activeIndex);
      dispose();
    }
  };

  const filterFormsMap: { [key in EntityType]?: ComponentType<any> } = {
    Video: VideosFilterForm,
    Survey: SurveyFilterForm,
    Assessment: AssessmentFilterForm,
    ExternalEvent: EventFilterForm,
    Email: EmailFilterForm,
    Pdf: PdfsFilterForm,
    Message: MessagesFilterForm,
    EmbeddedVideo: EmbeddedVideosFilterForm,
    Link: LinkFilterForm,
  };

  const embeddedVideosEnabled = useFeatureFlag(FeatureFlags.EmbeddedVideos);
  const linkAssetTypeEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);

  const contentViewTabs: ContentTab[] = useMemo(() => {
    return getContentViewTabsOptions(embeddedVideosEnabled, linkAssetTypeEnabled).map((tab) => ({
      label: tab.label,
      id: tab.id,
      component: (
        <PanelComponent
          id={tab.id}
          className="flow-components-section"
          key={tab.id}
          permissions={tab.permissions}
          onFilterButtonClick={() => setShowFilter(true)}
          appliedFilters={filters.appliedFilter}
          resetAppliedFilter={resetAppliedFilters}
          renderCard={tab.renderCard(isReadOnly)}
          filterOptions={tab.filterOptions}
          loadItems={loadItemsV2}
          itemsToDrop={itemsToDrop}
          search={search}
          entityTypeSelect={entityTypeSelect}
          changeEntityType={changeEntityType}
          setSearch={setSearch}
          resetItems={resetItems}
          dispose={dispose}
          dragNDropItemType={DragNDropItemTypes.DESIGNER}
          showPurchased
        />
      ),
    }));
  }, [
    filters.appliedFilter,
    resetAppliedFilters,
    isReadOnly,
    loadItemsV2,
    itemsToDrop,
    search,
    entityTypeSelect,
    changeEntityType,
    setSearch,
    resetItems,
    dispose,
    embeddedVideosEnabled,
    linkAssetTypeEnabled,
  ]);

  return (
    <div className={"flow-content-view"}>
      <ContentView tabs={contentViewTabs} onTabChange={onTabChange} activeIndex={activeViewTab} />

      <FlowContentFilterSidebarContainer
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterFormsMap={filterFormsMap}
      />
    </div>
  );
}

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isReadOnly: isFlowReadOnlySelector(state),
  activeViewTab: activeViewTabSelector(state),
  filters: flowDesignerSelector(state).filters,
  itemsToDrop: itemsToDropSelector(state),
  search: itemsToDropFiltersSelector(state).search,
  entityTypeSelect: flowInspectorTypeSelectSelector(state),
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    changeViewTab: bindAction(setActiveTab, dispatch),
    resetAppliedFilters: bindAction(resetAppliedFilter, dispatch),
    loadItemsV2: bindAction(fetchItemsToDropV2, dispatch),
    changeEntityType: bindAction(setEntityType, dispatch),
    setSearch: bindAction(setSearchAction, dispatch),
    resetItems: bindAction(resetItemsAction, dispatch),
    dispose: () =>
      batch(() => {
        dispatch(resetFilter());
        dispatch(resetItemsAction());
      }),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(FlowContentView);
export default ConnectedComponent;
